// src/pages/dataInput/RelationshipEditor.tsx
import React, { useEffect, useMemo, useState } from "react";
import { cls } from "../theme/theme";
import { Relationship, UploadedFile, SheetConfig } from "./types";
import { suggestFKs, buildFKSourcesFromFiles, FKSuggestion } from "./utils/fkSuggester";

interface AvailableTable {
  prefix:  string;
  label:   string;
  columns: string[];
}

interface Props {
  availableTables: AvailableTable[];
  /** Existing relationships (pre-populated from DDL/DB or user adds). */
  relationships:   Relationship[];
  onChange:        (rels: Relationship[]) => void;

  /**
   * Optional inputs that let us run the auto-suggester.
   * Only file source provides files+sheetConfigs; DB source already has
   * authoritative FKs from INFORMATION_SCHEMA.
   */
  files?:          UploadedFile[];
  sheetConfigs?:   SheetConfig[];
  enableAutoSuggest?: boolean;
}

export const RelationshipEditor: React.FC<Props> = ({
  availableTables,
  relationships,
  onChange,
  files,
  sheetConfigs,
  enableAutoSuggest = false,
}) => {
  const [suggestions, setSuggestions] = useState<FKSuggestion[]>([]);
  const [analysing,   setAnalysing]   = useState(false);
  const [hasRun,      setHasRun]      = useState(false);

  // Build column lookup
  const columnsByPrefix = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const t of availableTables) m[t.prefix] = t.columns;
    return m;
  }, [availableTables]);

  // ── Run auto-suggester ────────────────────────────────────────────────────
  const runSuggester = async () => {
    if (!files || !sheetConfigs || availableTables.length < 2) return;
    setAnalysing(true);
    try {
      // Build SourceForFK list: one entry per included sheet (or per CSV)
      const fkInputs = sheetConfigs
        .filter(sc => sc.included)
        .map(sc => {
          const f = files.find(x => x.name === sc.fileName);
          if (!f) return null;
          return {
            file:      f.file,
            sheetName: sc.sheetName,
            prefix:    (sc.displayName || sc.sheetName).toUpperCase().replace(/\s+/g, "_"),
            label:     sc.displayName || sc.sheetName,
          };
        })
        .filter(Boolean) as any[];

      const sources    = await buildFKSourcesFromFiles(fkInputs);
      const suggestSet = suggestFKs(sources, { minConfidence: 60 });
      setSuggestions(suggestSet);
    } catch (err) {
      console.warn("[RelationshipEditor] suggester failed:", err);
    } finally {
      setAnalysing(false);
      setHasRun(true);
    }
  };

  // Auto-run once when enabled
  useEffect(() => {
    if (enableAutoSuggest && !hasRun && availableTables.length >= 2 && files && sheetConfigs) {
      runSuggester();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAutoSuggest, files, sheetConfigs, availableTables.length]);

  // ── Mutators ─────────────────────────────────────────────────────────────
  const addManual = () => {
    onChange([
      ...relationships,
      {
        from_table:  availableTables[0]?.prefix || "",
        from_column: "",
        to_table:    availableTables[1]?.prefix || availableTables[0]?.prefix || "",
        to_column:   "",
        source:      "manual",
      },
    ]);
  };

  const removeAt = (idx: number) => {
    onChange(relationships.filter((_, i) => i !== idx));
  };

  const patchAt = (idx: number, patch: Partial<Relationship>) => {
    onChange(relationships.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const acceptSuggestion = (s: FKSuggestion) => {
    // Don't double-add
    const exists = relationships.find(r =>
      r.from_table === s.from_table && r.from_column === s.from_column &&
      r.to_table   === s.to_table   && r.to_column   === s.to_column
    );
    if (exists) {
      // Just remove from suggestions
      setSuggestions(prev => prev.filter(x => x !== s));
      return;
    }
    onChange([...relationships, {
      from_table:  s.from_table,
      from_column: s.from_column,
      to_table:    s.to_table,
      to_column:   s.to_column,
      source:      "suggested",
      confidence:  s.confidence,
      reason:      s.reason,
    }]);
    setSuggestions(prev => prev.filter(x => x !== s));
  };

  const rejectSuggestion = (s: FKSuggestion) => {
    setSuggestions(prev => prev.filter(x => x !== s));
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (availableTables.length < 2) {
    return null;  // FK editor doesn't show with a single source
  }

  return (
    <div className="gw-rel-editor">
      <div className="gw-rel-hdr">
        <span className="gw-rel-hdr-title">
          🔗 Relationships
          {relationships.length > 0 && ` · ${relationships.length}`}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          {enableAutoSuggest && (
            <button
              className="gw-btn gw-btn-ghost gw-btn-sm"
              onClick={runSuggester}
              disabled={analysing}
            >
              {analysing ? "Analysing…" : hasRun ? "Re-scan" : "🔍 Auto-detect"}
            </button>
          )}
          <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={addManual}>
            + Add manually
          </button>
        </div>
      </div>

      {/* Suggestions banner */}
      {suggestions.length > 0 && (
        <div className="gw-rel-suggest-banner">
          <span>
            💡 <strong>{suggestions.length}</strong> potential relationship
            {suggestions.length === 1 ? "" : "s"} detected from sample data
          </span>
        </div>
      )}

      {suggestions.map((s, i) => {
        const fromCols = columnsByPrefix[s.from_table] || [];
        const toCols   = columnsByPrefix[s.to_table] || [];
        return (
          <div key={`sug-${i}`} className="gw-rel-row suggested">
            <span className="gw-mode-badge typed" style={{ flexShrink: 0 }}>auto</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5 }}>
              {s.from_table}.<strong>{s.from_column}</strong>
            </span>
            <span className="gw-rel-arrow">⇌</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5 }}>
              {s.to_table}.<strong>{s.to_column}</strong>
            </span>
            <span className="gw-rel-conf">{s.confidence}%</span>
            <span style={{ flex: 1, fontSize: 11, color: "#445566" }}>{s.reason}</span>
            <div className="gw-rel-actions">
              <button
                className="gw-rel-icon-btn"
                title="Accept"
                onClick={() => acceptSuggestion(s)}
                style={{ color: "#166534" }}
              >✓</button>
              <button
                className="gw-rel-icon-btn danger"
                title="Reject"
                onClick={() => rejectSuggestion(s)}
              >✕</button>
            </div>
          </div>
        );
      })}

      {/* Confirmed / manual relationships */}
      {relationships.map((r, i) => {
        const fromCols = columnsByPrefix[r.from_table] || [];
        const toCols   = columnsByPrefix[r.to_table] || [];
        return (
          <div key={`rel-${i}`} className={cls("gw-rel-row", r.source !== "manual" && "confirmed")}>
            <span
              className={cls("gw-mode-badge", r.source === "manual" ? "raw" : "enriched")}
              style={{ flexShrink: 0 }}
              title={r.reason || `Source: ${r.source}`}
            >
              {r.source === "ddl" ? "ddl" :
               r.source === "db"  ? "db"  :
               r.source === "suggested" ? `auto·${r.confidence ?? "?"}%` : "manual"}
            </span>

            <select
              className="gw-rel-select"
              value={r.from_table}
              onChange={e => patchAt(i, { from_table: e.target.value, from_column: "" })}
            >
              {availableTables.map(t => (
                <option key={t.prefix} value={t.prefix}>{t.label}</option>
              ))}
            </select>

            <select
              className="gw-rel-select"
              value={r.from_column}
              onChange={e => patchAt(i, { from_column: e.target.value })}
              style={{ maxWidth: 160 }}
            >
              <option value="">— column —</option>
              {fromCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <span className="gw-rel-arrow">⇌</span>

            <select
              className="gw-rel-select"
              value={r.to_table}
              onChange={e => patchAt(i, { to_table: e.target.value, to_column: "" })}
            >
              {availableTables.map(t => (
                <option key={t.prefix} value={t.prefix}>{t.label}</option>
              ))}
            </select>

            <select
              className="gw-rel-select"
              value={r.to_column}
              onChange={e => patchAt(i, { to_column: e.target.value })}
              style={{ maxWidth: 160 }}
            >
              <option value="">— column —</option>
              {toCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <button
              className="gw-rel-icon-btn danger"
              onClick={() => removeAt(i)}
              title="Remove"
            >✕</button>
          </div>
        );
      })}

      {relationships.length === 0 && suggestions.length === 0 && (
        <div style={{ padding: "16px 14px", fontSize: 12, color: "#445566", textAlign: "center" }}>
          No relationships defined.
          {enableAutoSuggest && availableTables.length >= 2 && !analysing && hasRun && (
            <> Try auto-detect, or add manually.</>
          )}
          {!enableAutoSuggest && availableTables.length >= 2 && (
            <> Add manually if your data has cross-table joins.</>
          )}
        </div>
      )}
    </div>
  );
};

export default RelationshipEditor;